import React, { useState, useEffect } from 'react';
import { Brain, TrendingUp, Target, Zap, Crown, BarChart3, Activity } from 'lucide-react';
import { database } from '../../Config/firebase';
import { ref, onValue, push, set } from '../../Config/matuRtdb';

const AI_Predictions = () => {
  const [aiPredictions, setAiPredictions] = useState([]);
  const [userStats, setUserStats] = useState({
    accuracy: 0,
    totalPredictions: 0,
    streak: 0,
    aiAssistedWins: 0
  });
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);

  // Análisis de IA simulado (en producción usarías una API real)
  const analyzeMatch = (match) => {
    const factors = {
      homeForm: Math.random() * 100,
      awayForm: Math.random() * 100,
      headToHead: Math.random() * 100,
      injuries: Math.random() * 100,
      motivation: Math.random() * 100,
      weather: Math.random() * 100,
      referee: Math.random() * 100
    };

    const confidence = Object.values(factors).reduce((a, b) => a + b, 0) / 7;
    
    return {
      factors,
      confidence: Math.round(confidence),
      recommendation: confidence > 70 ? 'STRONG' : confidence > 50 ? 'MEDIUM' : 'WEAK',
      predictedScore: `${Math.floor(Math.random() * 3) + 1}-${Math.floor(Math.random() * 3)}`,
      keyInsights: [
        'Equipo local en buena racha',
        'Lesiones importantes en visitante',
        'Historial favorable para local'
      ]
    };
  };

  const handleAIAnalysis = (match) => {
    setSelectedMatch(match);
    const analysis = analyzeMatch(match);
    setAiAnalysis(analysis);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Brain className="w-8 h-8 text-purple-400" />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              IA Predictions Pro
            </h1>
            <Crown className="w-8 h-8 text-yellow-400" />
          </div>
          <p className="text-gray-300 text-lg">
            Análisis avanzado con inteligencia artificial para maximizar tus ganancias
          </p>
        </div>

        {/* Stats del usuario */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-purple-800/50 backdrop-blur-sm rounded-xl p-4 border border-purple-600/30">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-purple-400" />
              <span className="text-sm text-gray-300">Precisión</span>
            </div>
            <div className="text-2xl font-bold text-purple-400">{userStats.accuracy}%</div>
          </div>
          
          <div className="bg-blue-800/50 backdrop-blur-sm rounded-xl p-4 border border-blue-600/30">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              <span className="text-sm text-gray-300">Total</span>
            </div>
            <div className="text-2xl font-bold text-blue-400">{userStats.totalPredictions}</div>
          </div>
          
          <div className="bg-green-800/50 backdrop-blur-sm rounded-xl p-4 border border-green-600/30">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              <span className="text-sm text-gray-300">Racha</span>
            </div>
            <div className="text-2xl font-bold text-green-400">{userStats.streak}</div>
          </div>
          
          <div className="bg-yellow-800/50 backdrop-blur-sm rounded-xl p-4 border border-yellow-600/30">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              <span className="text-sm text-gray-300">IA Wins</span>
            </div>
            <div className="text-2xl font-bold text-yellow-400">{userStats.aiAssistedWins}</div>
          </div>
        </div>

        {/* Análisis de IA */}
        {selectedMatch && aiAnalysis && (
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-gray-700/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Análisis IA - {selectedMatch.homeTeam.name} vs {selectedMatch.awayTeam.name}</h2>
              <button 
                onClick={() => setSelectedMatch(null)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Factores de análisis */}
              <div>
                <h3 className="text-lg font-semibold mb-4 text-purple-400">Factores de Análisis</h3>
                <div className="space-y-3">
                  {Object.entries(aiAnalysis.factors).map(([factor, value]) => (
                    <div key={factor} className="flex items-center justify-between">
                      <span className="text-gray-300 capitalize">{factor.replace(/([A-Z])/g, ' $1').trim()}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all"
                            style={{ width: `${value}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-400 w-8">{Math.round(value)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recomendación */}
              <div>
                <h3 className="text-lg font-semibold mb-4 text-blue-400">Recomendación IA</h3>
                <div className="space-y-4">
                  <div className={`p-4 rounded-xl border-2 ${
                    aiAnalysis.recommendation === 'STRONG' ? 'border-green-500 bg-green-500/10' :
                    aiAnalysis.recommendation === 'MEDIUM' ? 'border-yellow-500 bg-yellow-500/10' :
                    'border-red-500 bg-red-500/10'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Brain className="w-5 h-5" />
                      <span className="font-bold">Confianza: {aiAnalysis.confidence}%</span>
                    </div>
                    <div className={`text-lg font-bold ${
                      aiAnalysis.recommendation === 'STRONG' ? 'text-green-400' :
                      aiAnalysis.recommendation === 'MEDIUM' ? 'text-yellow-400' :
                      'text-red-400'
                    }`}>
                      {aiAnalysis.recommendation === 'STRONG' ? '💪 APUESTA FUERTE' :
                       aiAnalysis.recommendation === 'MEDIUM' ? '⚖️ APUESTA MODERADA' :
                       '⚠️ APUESTA DÉBIL'}
                    </div>
                  </div>

                  <div className="bg-gray-700/50 rounded-xl p-4">
                    <h4 className="font-semibold mb-2 text-gray-300">Predicción de Resultado</h4>
                    <div className="text-2xl font-bold text-center text-purple-400">
                      {aiAnalysis.predictedScore}
                    </div>
                  </div>

                  <div className="bg-gray-700/50 rounded-xl p-4">
                    <h4 className="font-semibold mb-2 text-gray-300">Insights Clave</h4>
                    <ul className="space-y-1">
                      {aiAnalysis.keyInsights.map((insight, index) => (
                        <li key={index} className="text-sm text-gray-300 flex items-center gap-2">
                          <Activity className="w-3 h-3 text-purple-400" />
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lista de partidos con análisis IA */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-center mb-6">Partidos con Análisis IA</h2>
          {aiPredictions.map((match) => (
            <div key={match.id} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-gray-700/50 hover:border-purple-500/50 transition-all">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <img src={match.homeTeam.logo} alt={match.homeTeam.name} className="w-8 h-8" />
                    <span className="font-semibold">{match.homeTeam.name}</span>
                  </div>
                  <span className="text-gray-400 font-bold">VS</span>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{match.awayTeam.name}</span>
                    <img src={match.awayTeam.logo} alt={match.awayTeam.name} className="w-8 h-8" />
                  </div>
                </div>
                
                <button
                  onClick={() => handleAIAnalysis(match)}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2"
                >
                  <Brain className="w-4 h-4" />
                  Analizar con IA
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AI_Predictions; 